import React, { FC, HTMLAttributes } from 'react'
import { ImageBox, Counter } from '@engyalo/storefront-components'
import { Button, Box, Heading } from '@chakra-ui/core'
import { Price } from '../Price'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'
import { Hook } from '@engyalo/storefront-types'
import {
  ICartDictionary,
  IProduct,
} from '@engyalo/storefront-types/lib/plugins/model'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { cartState, configState, orderConfigState } from '../../recoil/atoms'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import Plugin from '../../Plugin'
import { getActionScreen } from '../../utils/analytics'
import { useLocation } from 'react-router'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: IProduct
}

export const CartItem: FC<IProps> = ({ product }: IProps) => {
  const location = useLocation()
  const { t } = useTranslation()
  const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const config = useRecoilValue(configState)

  const getProductFirstImage = (product: IProduct): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const addToCart = (product: IProduct, adder = 1): void => {
    const tempCart = { ...cart }
    if (product && product.id) {
      const currentSku = generateCartId(product)
      if (tempCart[currentSku]) {
        const tempProd = { ...tempCart[currentSku] }
        if (tempProd.quantity) {
          const totalPrice = getTotalPrice(product)
          tempProd.quantity += adder
          if (tempProd.quantity > 0) {
            tempCart[currentSku] = {
              ...tempProd,
              totalPrice,
            }
            setCart(tempCart)
          } else {
            delete tempCart[currentSku]
            setCart({ ...tempCart })
          }
        }
      } else if (adder > 0) {
        const totalPrice = getTotalPrice(product)
        tempCart[currentSku] = {
          ...product,
          totalPrice,
          quantity: adder,
          cartId: currentSku,
        } as IProduct
      }
      setCart(tempCart)
    }
  }

  const deleteItem = (product: IProduct): void => {
    const tempCart = { ...cart }
    if (product && product.cartId) {
      const currentSku = product.cartId
      if (tempCart[currentSku]) {
        delete tempCart[currentSku]
      }
    }
    setCart(tempCart)
  }

  if (product) {
    return (
      <Box
        borderBottom="1px solid"
        borderColor="gray.200"
        display="flex"
        padding="8px"
      >
        <Box flex="2">
          <Box display="flex">
            <Box w="40px">
              <ImageBox imageUrl={getProductFirstImage(product)} />
            </Box>
            <Box paddingLeft="8px">
              <Heading marginY="4px" as="h3" size="sm" flex="1">
                {product.name}
              </Heading>
              <Box
                fontSize="xs"
                fontFamily="body"
                color="gray.100"
                paddingBottom="4px"
              >
                {product.description}
              </Box>
              <Plugin
                hook={Hook.CART_ITEM_DETAIL}
                pluginProps={{ cart, product, tFunction: t }}
              >
                <></>
              </Plugin>
              <Price
                price={product.totalPrice ? product.totalPrice : product.price}
                currency={config.currencyPrefix}
              />
              <Button
                color="red.500"
                variant="link"
                border="transparent"
                fontSize="sm"
                textDecor="underline"
                backgroundColor="transparent"
                fontWeight="normal"
                onClick={(): void => {
                  window.analytics.track('Product Eliminated from Cart Start', {
                    'Product ID': product.id,
                    'Product SKU': product.sku,
                    'Product Name': product.name,
                    'Product Category': product.category,
                    'User ID': orderConfig.userID,
                    'Customer Name': orderConfig.channelID,
                  })
                  const shouldDeleteItem = window.confirm(
                    `Do you want to remove ${product.name} from cart?`
                  )
                  if (shouldDeleteItem) {
                    deleteItem(product)

                    window.analytics.track(
                      'Product Eliminated from Cart Completed',
                      {
                        'Product ID': product.id,
                        'Product SKU': product.sku,
                        'Product Name': product.name,
                        'Product Category': product.category,
                        'User ID': orderConfig.userID,
                        'Customer Name': orderConfig.channelID,
                      }
                    )
                  }
                }}
              >
                {t('cartItem-delete')}
              </Button>
            </Box>
          </Box>
        </Box>
        <Box flex="1">
          <Counter
            name={product.cartId || ''}
            addAction={(): void => {
              window.analytics.track('Product Added', {
                'Product ID': product.id,
                'Product SKU': product.sku,
                'Product Name': product.name,
                'Product Category': product.category,
                'Product Price': product.price,
                // 'Order Note': message,
                'Action Screen': getActionScreen(location),
                'User ID': orderConfig.userID,
                'Customer Name': orderConfig.channelID,
              })
              addToCart(product, 1)
            }}
            minusAction={(): void => {
              window.analytics.track('Product Removed', {
                'Product ID': product.id,
                'Product SKU': product.sku,
                'Product Name': product.name,
                'Product Category': product.category,
                'Product Price': product.price,
                // 'Order Note': '',
                'Action Screen': getActionScreen(location),
                'User ID': orderConfig.userID,
                'Customer Name': orderConfig.channelID,
              })
              addToCart(product, -1)
            }}
            value={
              cart[product.cartId || '']
                ? cart[product.cartId || ''].quantity
                : 0
            }
          />
        </Box>
      </Box>
    )
  }
  return null
}
